import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-expanded", "aria-controls", "aria-activedescendant", "data-placeholder", "aria-label"]
const _hoisted_2 = { class: "query-affordance" }
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = {
  key: 1,
  class: "icon icon-search"
}
const _hoisted_5 = {
  class: "sr-only",
  "aria-live": "polite",
  "aria-atomic": "true"
}
const _hoisted_6 = ["id", "aria-label", "aria-busy"]
const _hoisted_7 = {
  key: 0,
  class: "suggestion-rule",
  role: "presentation"
}
const _hoisted_8 = ["id", "aria-selected", "onMousedown", "onMouseenter"]
const _hoisted_9 = { class: "suggestion-detail" }
const _hoisted_10 = {
  key: 0,
  class: "suggestion-loading",
  role: "presentation",
  "data-testid": "table-views-suggestions-loading"
}

import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, watch
} from 'vue';
import { useStore } from 'vuex';

import RcSeparator from '@components/RcSeparator/RcSeparator.vue';
import { useI18n } from '@shell/composables/useI18n';
import { LABEL_FIELD_PREFIX, dateBuckets, valuesInUse } from '@shell/utils/table-views/fields';
import {
  CONNECTIVES, NEGATORS, highlightQuery, isNegator, quoteIfNeeded, replaceToken, scanQuery, tokenAt
} from '@shell/utils/table-views/query';
import { changeOf, createQueryHistory } from '@shell/utils/table-views/query-history';
import { isMac } from '@shell/utils/platform';
import type { TableViewField, TableViewRow } from '@shell/types/table-views';

const MENU_VIEWPORT_MARGIN = 20;

/** The stylesheet's `min-width`, to measure by before the list is on the page */
const MENU_MIN_WIDTH = 260;

/**
 * The list's border plus an option's padding, so the entry text rather than the box lines up with
 * the caret
 */
const MENU_TEXT_INSET = 13;

/** Beyond the list's edges, for its shadow, on a side that isn't cut */
const MENU_SHADOW_ROOM = 30;

const KEY_SEP = '\u0000';

interface Suggestion {
  key: string;
  label: string;
  detail?: string;
  insert: string;
  connective?: boolean;
  field?: TableViewField;
  value?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TableViewQueryInput',
  props: {
    value: { default: '' },
    fields: { default: () => [] },
    filterFields: { default: null },
    fieldValues: { default: () => ({}) },
    rows: { default: () => [] },
    dateFields: { default: () => [] },
    pendingFields: { default: () => [] },
    loadingValues: { default: () => [] },
    historyKey: { default: 0 }
  },
  emits: ["update:value", "request-values", "refresh-values", "update:focused"],
  setup(__props: any, { emit: __emit }) {

/**
 * The table views filter box: `field:value` terms and free text, suggesting fields and then values
 * in use. It edits its own rendered tokens, so a value badge's spacing is ordinary layout and the
 * caret stays true
 */
let uid = 0;

const props = __props;

const emit = __emit;

/** A field's values as the box offers them: the api's where it has them, else the page's; dates by month */
const valuesFor = (field: TableViewField) => {
  const fetched = props.fieldValues[field.id];

  if (props.dateFields.includes(field.id)) {
    return dateBuckets(fetched?.length ? fetched : valuesInUse(props.rows, field, Infinity));
  }

  return fetched?.length ? fetched : valuesInUse(props.rows, field);
};

const { t } = useI18n(useStore());

const root = ref<HTMLElement | null>(null);
const input = ref<HTMLElement | null>(null);
const menu = ref<HTMLElement | null>(null);

const caret = ref(0);
const focused = ref(false);
/** Escape hides the list without taking the caret out of the box */
const dismissed = ref(false);
const activeIndex = ref(0);
const pendingCaret = ref<number | null>(null);
const composing = ref(false);
/** Viewport coordinates: the menu hangs off <body> */
const menuPos = ref({
  top: 0, left: 0, width: 0, clip: 'none'
});
/** What the menu has to outrank, read as it opens: a slide-in panel sits above the product's z-index scale */
const menuZ = ref(0);
/** The box is scrolled out of sight, and with it the list */
const menuOutOfView = ref(false);
/**
 * Hidden until placed: a press that opens the list sets the caret after the box takes focus, so the
 * first placement would be at the old caret, then jump
 */
const menuPlaced = ref(false);
let placeFrame = 0;
/**
 * The box is ahead of the query between a keystroke and the re-render; redrawing then drops what
 * was typed
 */
const domAhead = ref(false);

/** Undo and redo of the box's own: the browser's can't follow contents rebuilt as they are highlighted */
const queryHistory = createQueryHistory(props.value || '');

const instanceId = `${ uid++ }`;

/** Per instance, as a page can hold more than one box */
const menuId = computed(() => `table-view-query-menu-${ instanceId }`);

const optionId = (index: number) => `${ menuId.value }-option-${ index }`;

const terms = computed(() => scanQuery(props.value || '', props.fields).filter((token) => token.kind === 'term'));

/** Worked out once per render: the page scan walks up to a thousand rows, and there is one term per badge */
const knownValues = computed(() => {
  const out: Record<string, Set<string>> = {};

  terms.value.forEach((term) => {
    const field = term.field;

    if (!field || out[field.id]) {
      return;
    }

    out[field.id] = new Set(valuesFor(field).map((entry) => entry.value.toLowerCase()));
  });

  return out;
});

const isKnownValue = (fieldId: string, value: unknown): boolean => {
  if (!value) {
    return false;
  }

  return !!knownValues.value[fieldId]?.has(`${ value }`.toLowerCase());
};

/** Set once the query has been shown: only the first showing waits */
const released = ref(false);

/**
 * As the page opens the query is held back while the values of a field it names are on their way:
 * shown then, its values would restyle as they land. Only a query the box was given; one being typed
 * is never hidden
 */
const holding = computed(() => !released.value && !focused.value && terms.value.some((term) => term.field && props.pendingFields.includes(term.field.id)));

watch(holding, (neu) => {
  if (!neu) {
    released.value = true;
  }
}, { immediate: true });

const segments = computed(() => highlightQuery(props.value || '', props.fields, (fieldId: string, value: unknown) => isKnownValue(fieldId, value)));

const queryFieldIds = computed(() => Array.from(new Set(terms.value.map((term) => term.field?.id).filter((id): id is string => !!id))));

const segmentsKey = computed(() => segments.value.map((segment) => `${ segment.kind }:${ segment.text }`).join(KEY_SEP));

const activeToken = computed(() => tokenAt(props.value || '', caret.value, props.fields));

const parsedToken = computed(() => {
  const token = activeToken.value;

  if (!token) {
    return {
      negate: '', field: null, typed: ''
    };
  }

  return {
    negate: token.negate || '',
    field:  token.field || null,
    // `value` rather than `text`, which still carries a leading `-` or `!`
    typed:  token.value,
  };
});

/** The finished token before the caret, which decides whether a joining word fits here */
const precedingToken = computed(() => {
  const active = activeToken.value;

  const before = scanQuery(props.value || '', props.fields)
    .filter((token) => token.end <= caret.value && (!active || token.start !== active.start));

  return before[before.length - 1] || null;
});

/** `not` can lead or follow anything finished; `and` and `or` need a finished term before them */
const connectiveSuggestions = computed<Suggestion[]>(() => {
  const { negate, field, typed } = parsedToken.value;

  if (field || negate) {
    return [];
  }

  const previous = precedingToken.value;
  let allowed;

  if (!previous) {
    allowed = NEGATORS;
  } else if (previous.kind === 'connective') {
    allowed = isNegator(previous.text) ? [] : NEGATORS;
  } else {
    allowed = [...CONNECTIVES, ...NEGATORS];
  }

  const needle = typed.toLowerCase();

  return allowed
    .filter((word: string) => word.includes(needle))
    .map((word: string) => ({
      key:        `connective:${ word }`,
      label:      word,
      detail:     t(`tableViews.query.connective.${ word }`),
      insert:     `${ word } `,
      connective: true,
    }));
});

const showClear = computed(() => !!props.value);

const suggestableFields = computed<TableViewField[]>(() => props.filterFields || props.fields);

/**
 * The field typed while the api is asked for its values: nothing is offered until they come, rather than
 * the page's and then the api's. The row saying so is not an option, as there is nothing to pick
 */
const loadingValuesFor = computed(() => {
  const { field } = parsedToken.value;

  // Only a field whose values are offered: for another, nothing would follow the row
  return field && props.loadingValues.includes(field.id) && suggestableFields.value.some((f) => f.id === field.id) ? field : null;
});

const suggestions = computed<Suggestion[]>(() => {
  const { negate, field, typed } = parsedToken.value;
  const needle = typed.toLowerCase();

  if (field) {
    // The page scan would offer real-looking values for a term that is then ignored
    if (!suggestableFields.value.some((f) => f.id === field.id) || loadingValuesFor.value) {
      return [];
    }

    return valuesFor(field)
      .filter((entry) => entry.value.toLowerCase().includes(needle))
      .map((entry) => ({
        key:    `${ field.id }:${ entry.value }`,
        label:  entry.value,
        detail: t('tableViews.query.inUse', { count: entry.count }),
        insert: `${ negate }${ field.id }:${ quoteIfNeeded(entry.value) } `,
        value:  entry.value,
      }));
  }

  return connectiveSuggestions.value.concat(suggestableFields.value
    .filter((f) => f.id.toLowerCase().includes(needle) || f.label.toLowerCase().includes(needle))
    .slice(0, 20)
    .map((f) => ({
      key:    f.id,
      label:  f.isLabel ? `${ LABEL_FIELD_PREFIX }${ f.label }` : f.label,
      detail: f.isLabel ? t('tableViews.query.label') : f.id,
      insert: `${ negate }${ f.id }:`,
      field:  f,
    })));
});

/**
 * Split into runs where the kind changes, for the rules between them. Each keeps its flat index for
 * the keyboard
 */
const suggestionGroups = computed(() => {
  const kindOf = (suggestion: Suggestion) => {
    if (suggestion.connective) {
      return 'joiner';
    }

    return suggestion.field?.isLabel ? 'label' : 'field';
  };

  type Group = { kind: string, entries: (Suggestion & { index: number })[] };
  const groups: Group[] = [];
  let current: Group | null = null;

  suggestions.value.forEach((suggestion, index) => {
    const kind = kindOf(suggestion);

    if (!current || current.kind !== kind) {
      current = { kind, entries: [] };
      groups.push(current);
    }

    current.entries.push({ ...suggestion, index });
  });

  return groups;
});

const suggestionsKey = computed(() => suggestions.value.map((suggestion) => suggestion.key).join(KEY_SEP));

const showSuggestions = computed(() => focused.value && !dismissed.value && (!!suggestions.value.length || !!loadingValuesFor.value));

const activeDescendantId = computed(() => (showSuggestions.value && suggestions.value.length ? optionId(activeIndex.value) : undefined));

/** A combobox announces its active option, but not that a list appeared or how long it is */
const suggestionsAnnouncement = computed(() => {
  if (!showSuggestions.value) {
    return '';
  }

  if (!suggestions.value.length) {
    return t('tableViews.query.loadingValues');
  }

  return t('tableViews.query.suggestionsAvailable', { count: suggestions.value.length }, true);
});

const menuStyle = computed(() => ({
  // Width is the stylesheet's: as wide as the entries need
  top:                  `${ menuPos.value.top }px`,
  left:                 `${ menuPos.value.left }px`,
  clipPath:             menuPos.value.clip,
  '--query-menu-stack': menuZ.value,
  ...(menuPlaced.value ? {} : { visibility: 'hidden' as const }),
}));

/** Counted across the token spans, so it is an offset into the query string */
const readCaret = (): number | null => {
  const el = input.value;
  const selection = window.getSelection();

  if (!el || !selection?.rangeCount) {
    return null;
  }

  const range = selection.getRangeAt(0);

  // A stale offset would put the next keystroke at the start, typing text backwards
  if (!el.contains(range.endContainer)) {
    return null;
  }

  const upto = range.cloneRange();

  upto.selectNodeContents(el);
  upto.setEnd(range.endContainer, range.endOffset);

  return upto.toString().length;
};

const applyCaret = (offset: number) => {
  const el = input.value;

  if (!el) {
    return;
  }

  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let remaining = Math.max(0, Math.min(offset, (el.textContent || '').length));
  let node = walker.nextNode();
  let last: Node | null = null;

  while (node) {
    if (remaining <= (node.textContent || '').length) {
      range.setStart(node, remaining);
      break;
    }

    remaining -= (node.textContent || '').length;
    last = node;
    node = walker.nextNode();
  }

  if (!node) {
    if (last) {
      range.setStart(last, (last.textContent || '').length);
    } else {
      range.selectNodeContents(el);
    }
  }

  range.collapse(true);

  const selection = window.getSelection();

  selection?.removeAllRanges();
  selection?.addRange(range);
  caret.value = Math.max(0, Math.min(offset, (el.textContent || '').length));
  keepCaretInView(range);
};

/** A caret placed from code isn't scrolled to, as a typed one is */
const keepCaretInView = (range: Range) => {
  const el = input.value;
  const at = range.getBoundingClientRect();

  if (!el || !(at.left || at.top)) {
    return;
  }

  const box = el.getBoundingClientRect();

  if (at.left > box.right) {
    el.scrollLeft += at.left - box.right + 1;
  } else if (at.left < box.left) {
    el.scrollLeft -= box.left - at.left;
  }
};

/** Rebuild the box from the query, browser inserted nodes included */
const syncDom = () => {
  const el = input.value;

  if (!el || composing.value) {
    return;
  }

  // A keystroke still on its way back; leave what the user sees alone
  if (domAhead.value && el.textContent !== (props.value || '')) {
    return;
  }

  domAhead.value = false;

  while (el.firstChild) {
    el.removeChild(el.firstChild);
  }

  segments.value.forEach((segment) => {
    const span = document.createElement('span');

    span.className = `segment-${ segment.kind }`;
    span.textContent = segment.text;
    el.appendChild(span);
  });

  // Not `focused`, which trails a blur by 150ms
  if (document.activeElement === el) {
    applyCaret(pendingCaret.value ?? caret.value);
  }

  pendingCaret.value = null;
};

/**
 * Browsers give no rect for some collapsed ranges; the box's left edge is the caret in exactly
 * those cases
 */
const caretLeft = (): number | null => {
  const el = input.value;

  if (!el) {
    return null;
  }

  const selection = window.getSelection();

  if (selection?.rangeCount) {
    const range = selection.getRangeAt(0);

    if (el.contains(range.startContainer)) {
      const rect = range.getBoundingClientRect();

      if (rect && (rect.left || rect.top)) {
        return rect.left;
      }
    }
  }

  return el.getBoundingClientRect().left;
};

/** The highest z-index among the box's ancestors, since the menu is their sibling on <body> */
const stackAbove = () => {
  let el = root.value?.parentElement;
  let highest = 0;

  while (el && el !== document.documentElement) {
    const z = parseInt(getComputedStyle(el).zIndex, 10);

    if (!isNaN(z) && z > highest) {
      highest = z;
    }

    el = el.parentElement;
  }

  return highest ? highest + 1 : 0;
};

/** Where the box can be seen: the viewport, less what its scrolling ancestors cut off */
const visibleArea = () => {
  const area = {
    top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth
  };

  for (let el = root.value?.parentElement; el && el !== document.body; el = el.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(el);

    if (overflowX !== 'visible' || overflowY !== 'visible') {
      const clip = el.getBoundingClientRect();

      area.top = Math.max(area.top, clip.top);
      area.left = Math.max(area.left, clip.left);
      area.bottom = Math.min(area.bottom, clip.bottom);
      area.right = Math.min(area.right, clip.right);
    }
  }

  return area;
};

const updateMenuPos = () => {
  const rect = root.value?.getBoundingClientRect?.();

  if (rect) {
    // The size is only known once rendered
    const width = menu.value?.offsetWidth || MENU_MIN_WIDTH;
    const height = menu.value?.offsetHeight || 0;
    const rightmost = Math.max(MENU_VIEWPORT_MARGIN, window.innerWidth - width - MENU_VIEWPORT_MARGIN);
    // Over the box's bottom border, so the two 1px edges meet as one line
    const top = rect.bottom - 1;
    const left = Math.min((caretLeft() ?? rect.left) - MENU_TEXT_INSET, rightmost);
    const area = visibleArea();
    // The list hangs off <body>, so it can't pass under the header and nav as the box does; it is
    // cut where they start instead. A side with nothing to cut leaves room for the shadow
    const cut = [area.top - top, left + width - area.right, top + height - area.bottom, area.left - left];

    menuPos.value = {
      top,
      left,
      width: rect.width,
      clip:  cut.some((px) => px > 0) ? `inset(${ cut.map((px) => (px > 0 ? `${ Math.ceil(px) }px` : `-${ MENU_SHADOW_ROOM }px`)).join(' ') })` : 'none',
    };
    menuOutOfView.value = rect.bottom <= area.top || rect.bottom > area.bottom || rect.right <= area.left || rect.left >= area.right;
  }

  menuZ.value = stackAbove();
};

const repositionMenu = () => {
  if (showSuggestions.value) {
    nextTick(() => updateMenuPos());
  }
};

const syncCaret = () => {
  const at = readCaret();

  if (at !== null) {
    caret.value = at;
  }
};

const focus = () => input.value?.focus();

/** No event when a composition ends, which commits letters typed */
const onInput = (event?: Event) => {
  if (composing.value) {
    return;
  }

  dismissed.value = false;

  const text = input.value?.textContent ?? '';

  caret.value = readCaret() ?? text.length;
  pendingCaret.value = caret.value;
  domAhead.value = true;
  queryHistory.record(text, event ? changeOf((event as InputEvent).inputType) : 'insert');
  emit('update:value', text);
};

const onCompositionEnd = () => {
  composing.value = false;
  onInput();
};

/** Flattened to one line and spliced in over the selection */
const onPaste = (event: ClipboardEvent) => {
  const text = event.clipboardData?.getData('text') || '';
  const flat = text.replace(/\s+/g, ' ').trim();
  const el = input.value;

  if (!el) {
    return;
  }

  const current = props.value || '';
  const selection = window.getSelection();
  const range = selection?.rangeCount ? selection.getRangeAt(0) : null;

  // Only a selection inside this box counts; one left elsewhere by copying means nothing here
  let start = Math.min(caret.value ?? current.length, current.length);
  let end = start;

  if (range && el.contains(range.startContainer) && el.contains(range.endContainer)) {
    const before = range.cloneRange();

    before.selectNodeContents(el);
    before.setEnd(range.startContainer, range.startOffset);

    start = before.toString().length;
    end = start + range.toString().length;
  }

  const next = `${ current.substring(0, start) }${ flat }${ current.substring(end) }`;

  pendingCaret.value = start + flat.length;
  // Written by us, so the box is the stale one
  domAhead.value = false;
  queryHistory.record(next, 'other');
  emit('update:value', next);
};

const onFocus = () => {
  focused.value = true;
  dismissed.value = false;
  emit('update:focused', true);
};

/** Placed afresh even when the caret lands where it was, which the caret watcher wouldn't see */
const onClick = () => {
  dismissed.value = false;
  syncCaret();
  repositionMenu();
};

/**
 * Chrome puts a press on no text into the nearest editable spot, which in the toolbar is this box,
 * so presses on its ancestors are refused
 */
const onOutsideMouseDown = (event: MouseEvent) => {
  const el = root.value;
  const target = event.target as HTMLElement | null;

  if (!el || !target || el.contains(target) || menu.value?.contains?.(target)) {
    return;
  }

  if (target.contains?.(el)) {
    event.preventDefault();
  }

  focused.value = false;
  emit('update:focused', false);

  if (document.activeElement === input.value) {
    input.value?.blur();
  }
};

const onBlur = () => {
  // Let a click on a suggestion land first, and ignore a blur the box comes straight back from
  setTimeout(() => {
    if (document.activeElement !== input.value) {
      focused.value = false;
      emit('update:focused', false);
    }
  }, 150);
};

/** `dismissed` is left alone: Escape already closed the list, and the button never did */
const clear = () => {
  pendingCaret.value = 0;
  // Written by us, so the box is the stale one
  domAhead.value = false;
  queryHistory.record('', 'other');
  emit('update:value', '');
  focus();
};

/** Nothing is written around the pick, and a repeated term is allowed */
const pick = (suggestion?: Suggestion) => {
  if (!suggestion) {
    return;
  }

  const token = activeToken.value;
  const value = props.value || '';
  const next = replaceToken(value, token, suggestion.insert, caret.value);

  pendingCaret.value = token ? token.start + suggestion.insert.length : caret.value + (next.length - value.length);
  // Written by us, so the box is the stale one
  domAhead.value = false;
  queryHistory.record(next, 'other');
  emit('update:value', next);
  focus();
};

/** Arrowing scrolls the list under a still pointer, whose `mouseenter` would take the highlight back */
const keyboardNav = ref(false);

/** Wraps at either end, scrolling the entry into view */
const moveActive = (delta: number) => {
  const count = suggestions.value.length;

  if (!count) {
    return;
  }

  keyboardNav.value = true;
  activeIndex.value = (activeIndex.value + delta + count) % count;

  nextTick(() => {
    const list = menu.value;
    const at = activeIndex.value;

    if (!list) {
      return;
    }

    // `nearest` would leave the list's padding scrolled off at the ends
    if (at === 0 || at === suggestions.value.length - 1) {
      list.scrollTop = at === 0 ? 0 : list.scrollHeight;

      return;
    }

    list.querySelector(`[id="${ optionId(at) }"]`)?.scrollIntoView({ block: 'nearest' });
  });
};

const onSuggestionHover = (index: number) => {
  if (keyboardNav.value) {
    return;
  }

  activeIndex.value = index;
};

/** Steps the query back or on through the box's history */
const travel = (back: boolean) => {
  const entry = back ? queryHistory.undo() : queryHistory.redo();

  if (!entry) {
    return;
  }

  pendingCaret.value = entry.caret;
  // Written by us, so the box is the stale one
  domAhead.value = false;
  emit('update:value', entry.text);
};

/** Cmd+Z / Cmd+Shift+Z on a Mac, Ctrl+Z / Ctrl+Shift+Z or Ctrl+Y elsewhere */
const historyStep = (event: KeyboardEvent): 'undo' | 'redo' | null => {
  const mod = isMac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
  const key = event.key.toLowerCase();

  if (!mod || event.altKey || composing.value) {
    return null;
  }

  if (key === 'z') {
    return event.shiftKey ? 'redo' : 'undo';
  }

  return !isMac && key === 'y' && !event.shiftKey ? 'redo' : null;
};

/** The Edit menu's Undo and Redo, which reach the box as input rather than keys */
const onBeforeInput = (event: InputEvent) => {
  if (event.inputType === 'historyUndo' || event.inputType === 'historyRedo') {
    event.preventDefault();
    travel(event.inputType === 'historyUndo');
  }
};

const onKeyDown = (event: KeyboardEvent) => {
  const step = historyStep(event);

  if (step) {
    event.preventDefault();
    travel(step === 'undo');

    return;
  }

  if (event.key === 'Enter') {
    event.preventDefault();
  }

  if (!showSuggestions.value) {
    if (event.key === 'Escape' && props.value) {
      event.preventDefault();
      clear();
    }

    return;
  }

  // A list that can't be seen can't be picked from
  if (menuOutOfView.value) {
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    moveActive(1);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    moveActive(-1);
  } else if (event.key === 'Enter') {
    event.preventDefault();

    if (suggestions.value[activeIndex.value]) {
      pick(suggestions.value[activeIndex.value]);
    }
  } else if (event.key === 'Escape') {
    dismissed.value = true;
  }
};

/**
 * Every field the query names, since a view applied from a tab arrives with nothing typed, and the
 * one being typed, whose values are about to be offered. Asked each time: the owner knows which it
 * has fetched, and the values it passes may be ones it only knows up front
 */
const wantedFieldIds = computed(() => Array.from(new Set([...queryFieldIds.value, parsedToken.value.field?.id].filter((id): id is string => !!id))).join(KEY_SEP));

watch(wantedFieldIds, (ids) => {
  ids.split(KEY_SEP).filter(Boolean).forEach((id: string) => emit('request-values', id));
}, { immediate: true });

/** The field whose values the box is offering, if any */
const offeredFieldId = computed(() => {
  const { field } = parsedToken.value;

  return field && suggestableFields.value.some((f) => f.id === field.id) ? field.id : null;
});

/** The field whose values were last asked for again since the box was focused or its list shown */
let refreshedFieldId: string | null = null;

/** As a field's values open: once while the list stays up, whatever is typed, and again once reopened */
watch([offeredFieldId, focused, dismissed], ([id, isFocused, isDismissed]) => {
  if (!isFocused || isDismissed) {
    refreshedFieldId = null;

    return;
  }

  if (id && id !== refreshedFieldId) {
    refreshedFieldId = id;
    emit('refresh-values', id);
  }
});

/** Keyed on the contents: the computed array is new whenever the caret moves */
watch(suggestionsKey, () => {
  activeIndex.value = 0;
  repositionMenu();
});

watch(caret, () => repositionMenu());

watch(showSuggestions, (open) => {
  cancelAnimationFrame(placeFrame);

  if (open) {
    menuPlaced.value = false;
    // A frame on, the press has put the caret down; the list is then built for it and placed
    placeFrame = requestAnimationFrame(() => {
      syncCaret();
      nextTick(() => {
        updateMenuPos();
        menuPlaced.value = true;
      });
    });
    window.addEventListener('scroll', updateMenuPos, true);
    window.addEventListener('resize', updateMenuPos);
  } else {
    window.removeEventListener('scroll', updateMenuPos, true);
    window.removeEventListener('resize', updateMenuPos);
  }
});

/** `segments` is rebuilt on every row update; redrawing then would yank the caret */
watch(segmentsKey, () => syncDom());

// Another tab's query, a reset or a link: what came before it isn't this box's to undo back to
watch(() => props.value || '', (value) => {
  if (value !== queryHistory.current()) {
    queryHistory.reset(value);
  }
});

// Another view put on the table, even one with the same query
watch(() => props.historyKey, () => queryHistory.reset(props.value || ''));

onMounted(() => {
  syncDom();
  // Capture, so it is seen before the browser decides what the press means
  document.addEventListener('mousedown', onOutsideMouseDown, true);
});

onBeforeUnmount(() => {
  cancelAnimationFrame(placeFrame);
  document.removeEventListener('mousedown', onOutsideMouseDown, true);
  window.removeEventListener('scroll', updateMenuPos, true);
  window.removeEventListener('resize', updateMenuPos);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: _normalizeClass(["table-view-query", { focused: focused.value, 'list-open': showSuggestions.value }])
  }, [
    _createElementVNode("div", {
      ref_key: "input",
      ref: input,
      class: _normalizeClass(["query-input", { 'is-empty': !__props.value, holding: holding.value }]),
      contenteditable: "true",
      role: "combobox",
      "aria-multiline": "false",
      "aria-autocomplete": "list",
      "aria-expanded": showSuggestions.value ? 'true' : 'false',
      "aria-controls": menuId.value,
      "aria-activedescendant": activeDescendantId.value,
      spellcheck: "false",
      "data-testid": "table-views-query",
      "data-placeholder": _unref(t)('tableViews.query.placeholder'),
      "aria-label": _unref(t)('tableViews.query.placeholder'),
      onBeforeinput: onBeforeInput,
      onInput: onInput,
      onPaste: _withModifiers(onPaste, ["prevent"]),
      onCompositionstart: _cache[0] || (_cache[0] = ($event: any) => (composing.value = true)),
      onCompositionend: onCompositionEnd,
      onClick: onClick,
      onKeyup: syncCaret,
      onKeydown: onKeyDown,
      onFocus: onFocus,
      onBlur: onBlur
    }, null, 42, _hoisted_1),
    _cache[7] || (_cache[7] = _createTextVNode()),
    _createElementVNode("span", _hoisted_2, [
      (showClear.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "button",
            class: "query-clear",
            "aria-label": _unref(t)('tableViews.query.clear'),
            "data-testid": "table-views-query-clear",
            onMousedown: _withModifiers(clear, ["left","prevent"])
          }, [...(_cache[2] || (_cache[2] = [
            _createElementVNode("i", { class: "icon icon-close" }, null, -1)
          ]))], 40, _hoisted_3))
        : (!__props.value)
          ? (_openBlock(), _createElementBlock("i", _hoisted_4))
          : _createCommentVNode("", true)
    ]),
    _cache[8] || (_cache[8] = _createTextVNode()),
    _createElementVNode("span", _hoisted_5, _toDisplayString(suggestionsAnnouncement.value), 1),
    _cache[9] || (_cache[9] = _createTextVNode()),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (showSuggestions.value)
        ? (_openBlock(), _createElementBlock("ul", {
            key: 0,
            id: menuId.value,
            ref_key: "menu",
            ref: menu,
            class: "vs__dropdown-menu table-view-query-menu",
            role: "listbox",
            "aria-label": _unref(t)('tableViews.query.suggestions'),
            "aria-busy": !!loadingValuesFor.value,
            style: _normalizeStyle(menuStyle.value),
            "data-testid": "table-views-suggestions",
            onMousemove: _cache[1] || (_cache[1] = ($event: any) => (keyboardNav.value = false))
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(suggestionGroups.value, (group, g) => {
              return (_openBlock(), _createElementBlock(_Fragment, {
                key: group.kind
              }, [
                g
                  ? (_openBlock(), _createElementBlock("li", _hoisted_7, [
                      _createVNode(RcSeparator)
                    ]))
                  : _createCommentVNode("", true),
                _cache[4] || (_cache[4] = _createTextVNode()),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.entries, (entry) => {
                  return (_openBlock(), _createElementBlock("li", {
                    id: optionId(entry.index),
                    key: entry.key,
                    role: "option",
                    "aria-selected": entry.index === activeIndex.value ? 'true' : 'false',
                    class: _normalizeClass({ active: entry.index === activeIndex.value }),
                    onMousedown: _withModifiers(($event: any) => (pick(entry)), ["prevent"]),
                    onMouseenter: ($event: any) => (onSuggestionHover(entry.index))
                  }, [
                    _createElementVNode("span", {
                      class: _normalizeClass(["suggestion-label", { connective: entry.connective }])
                    }, _toDisplayString(entry.label), 3),
                    _cache[3] || (_cache[3] = _createTextVNode()),
                    _createElementVNode("span", _hoisted_9, _toDisplayString(entry.detail), 1)
                  ], 42, _hoisted_8))
                }), 128))
              ], 64))
            }), 128)),
            _cache[6] || (_cache[6] = _createTextVNode()),
            (loadingValuesFor.value)
              ? (_openBlock(), _createElementBlock("li", _hoisted_10, [
                  _cache[5] || (_cache[5] = _createElementVNode("i", { class: "icon icon-spinner icon-spin" }, null, -1)),
                  _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.query.loadingValues')), 1)
                ]))
              : _createCommentVNode("", true)
          ], 44, _hoisted_6))
        : _createCommentVNode("", true)
    ]))
  ], 2))
}
}

})